import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { imgUrl, logoUrl, assetUrl, runJob } from "@/lib/glossyApi";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LayoutGrid, Users, Newspaper, Share2, Sparkles, Image as ImageIcon,
  Check, Trash2, RefreshCw, Send, X, LogOut, Plus, Pencil, Upload, Save, Eye, Megaphone, Star, PenLine, Zap, Gift, ArrowLeft, Tag, ExternalLink, Table2, Archive as ArchiveIcon, Clock, ChevronUp, ChevronDown, FileText, Globe, ShieldCheck,
} from "lucide-react";
import { LONDON_AREAS } from "@/lib/londonAreas";
import ShareKit from "@/components/ShareKit";
import WebsiteImport, { mergeProfile } from "@/components/WebsiteImport";
import { cycleIndexFor, editionLabel, editionStartIso, issueNoFor, upcomingEditions } from "@/lib/issue";
import { AD_SLOT_META, CATEGORIES, SECTIONS, TONES, LENGTHS, MCATS, BUSINESS_CATS, Header, Stat, InboxGroup, InboxRow, DIn, DArea, Badge, Panel, Drawer, Field, Area, Select, ComboField } from "./shared";

export default function Articles({ scope = "all" }) {
  const [articles, setArticles] = useState([]);
  const [members, setMembers] = useState([]);
  const [brief, setBrief] = useState("");
  const [category, setCategory] = useState("Trends");
  const [kind, setKind] = useState(scope === "members" ? "feature" : "editorial");
  const [memberId, setMemberId] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [busy, setBusy] = useState("");
  const [editor, setEditor] = useState(null);
  const [checkOpen, setCheckOpen] = useState("");
  const [checking, setChecking] = useState("");
  const [batch, setBatch] = useState(null);

  const pollBatch = async () => {
    try {
      const r = await client.get("/admin/newsroom/commission-nine/status");
      setBatch(r.data);
      if (r.data.status === "running") setTimeout(pollBatch, 5000);
      else if (r.data.status === "done") { load(); }
    } catch {}
  };

  const commissionNine = async () => {
    if (!window.confirm("Commission 9 newsroom articles at once?\n\nThe desk scans what's trending for UK small businesses right now, writes all 9 in the S/ME voice with real picture-desk photography and the right categories, and drops them here as DRAFTS. You then delete any you don't like or publish the lot. Takes ~5-10 minutes and spends AI credits.")) return;
    setBusy("nine");
    try {
      await client.post("/admin/newsroom/commission-nine");
      toast.success("The desk is on it — 9 pieces are being written. Watch the progress below.");
      pollBatch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start the batch."); }
    setBusy("");
  };

  useEffect(() => {
    if (scope === "newsroom") client.get("/admin/newsroom/commission-nine/status").then((r) => { setBatch(r.data); if (r.data.status === "running") setTimeout(pollBatch, 5000); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const runCheck = async (a) => {
    setChecking(a.id);
    setCheckOpen(a.id);
    try {
      await client.post(`/admin/articles/${a.id}/editors-check`);
      toast.success("Editor's check complete.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Check failed — try again."); }
    setChecking("");
  };

  const loadLaunchNewsroom = async () => {
    if (!window.confirm("Load the 9 launch newsroom articles?\n\nThis ONLY touches the magazine's own newsroom: the 9 launch editorials go live and any other magazine editorials move to drafts. Members, cover star, letters and adverts are untouched.")) return;
    setBusy("launch");
    try {
      const r = await client.post("/admin/newsroom/load-launch");
      toast.success(`${r.data.loaded} launch articles live · ${r.data.unpublished_old} old newsroom pieces moved to drafts.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load the launch newsroom."); }
    setBusy("");
  };

  const load = () => client.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => {});
  useEffect(() => {
    load();
    client.get("/admin/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const inScope = (a) => (scope === "newsroom" ? !a.member_id : scope === "members" ? !!a.member_id : true);
  const list = articles.filter(inScope);

  const generate = async (publish) => {
    if (scope === "members" && !memberId) { toast.error("Pick which member the piece is about."); return; }
    if (!brief.trim() && !memberId) { toast.error("Add a brief, or pick a member to write about."); return; }
    setBusy(publish ? "pub" : "draft");
    try {
      const r = await runJob("/admin/generate/article", { kind, notes: brief, category, member_id: memberId || undefined, source_url: sourceUrl || undefined, publish });
      toast.success(publish ? "Written by AI & published." : "Written by AI — review the draft.");
      setBrief(""); setSourceUrl("");
      await load(); setEditor(r.data);
    } catch { toast.error("Generation failed — try again."); }
    setBusy("");
  };
  const togglePublish = async (a) => { await client.patch(`/admin/articles/${a.id}`, { status: a.status === "published" ? "draft" : "published" }); toast.success(a.status === "published" ? "Unpublished." : "Published."); load(); };
  const toggleArchive = async (a) => {
    await client.patch(`/admin/articles/${a.id}`, { is_archived: !a.is_archived });
    toast.success(a.is_archived ? "Restored to the live magazine." : "Moved to The Archive (still readable at its link).");
    load();
  };
  const remove = async (a) => { await client.delete(`/admin/articles/${a.id}`); toast.success("Deleted."); load(); };
  const move = async (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= list.length) return;
    const swapped = [...list]; [swapped[idx], swapped[j]] = [swapped[j], swapped[idx]];
    let k = 0;
    const full = articles.map((a) => (inScope(a) ? swapped[k++] : a));
    setArticles(full);
    try { await client.post("/admin/articles/reorder", { ids: full.map((x) => x.id) }); }
    catch { toast.error("Reorder failed."); load(); }
  };

  return (
    <div>
      <Header kicker={scope === "newsroom" ? "Your own articles — the magazine's voice" : scope === "members" ? "Features & columns about members" : "Editorial Engine"} title={scope === "newsroom" ? "The Newsroom" : scope === "members" ? "Member Pieces" : "The Edit"}>
        <button onClick={() => setEditor({ _new: true, title: "", dek: "", body: "", category: "Trends", tags: [], status: "draft" })} className="bg-ink text-cream px-5 py-3 overline flex items-center gap-2 hover:bg-gloss-silver hover:text-ink transition-colors" data-testid="new-article">
          <Plus size={16} /> Write manually
        </button>
      </Header>
      <div className="p-6 sm:p-10">
        <div className="bg-ink text-cream p-6 sm:p-8 grain mb-10" data-testid="generate-editorial-panel">
          <div className="relative z-10">
            <p className="overline text-gloss-gold mb-1">Create with AI</p>
            <p className="font-sans text-sm text-cream/60 mb-5">{scope === "newsroom" ? "Give a direction. The AI researches and writes a full, accurate, S/ME-voiced newsroom article. You review, edit and publish." : "Give a direction (or pick a member + their website). The AI researches and writes a full, accurate, S/ME-voiced article. You review, edit and publish."}</p>
            <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2} placeholder="Your brief / notes — e.g. 'Profile this baker, focus on how one loaf built a cult following' or 'Why independent cafés are the new offices'"
              className="w-full bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold transition-colors mb-4 resize-none" data-testid="editorial-brief" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              {scope !== "newsroom" && (
              <select value={kind} onChange={(e) => setKind(e.target.value)} className="bg-ink border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="editorial-kind">
                {scope !== "members" && <option value="editorial">Editorial feature</option>}
                <option value="feature">Member feature (profile)</option>
                <option value="blog">Member's Desk column</option>
              </select>
              )}
              {scope !== "newsroom" && (
              <select value={memberId} onChange={(e) => { const id = e.target.value; setMemberId(id); const m = members.find((x) => x.id === id); if (m?.website) setSourceUrl(m.website); }} className="bg-ink border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="editorial-member">
                <option value="">{scope === "members" ? "Which member is it about?" : "About a member? (optional)"}</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.business_name}</option>)}
              </select>
              )}
              <input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="Website to research (optional)" className="bg-transparent border border-cream/30 px-4 py-3 font-sans focus:outline-none focus:border-gloss-gold" data-testid="editorial-source" />
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="bg-ink border border-cream/30 px-4 py-3 font-sans focus:outline-none" data-testid="editorial-category">{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => generate(false)} disabled={!!busy} className="bg-cream text-ink px-6 py-3 overline flex items-center justify-center gap-2 hover:bg-gloss-gold transition-colors disabled:opacity-60" data-testid="generate-editorial-btn">
                {busy === "draft" ? <RefreshCw size={16} className="animate-spin" /> : <Sparkles size={16} />} Write draft
              </button>
              <button onClick={() => generate(true)} disabled={!!busy} className="border border-cream/60 px-6 py-3 overline flex items-center justify-center gap-2 hover:bg-cream hover:text-ink transition-colors disabled:opacity-60" data-testid="generate-publish-btn">
                {busy === "pub" ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />} Write & publish
              </button>
              {scope !== "members" && (
              <button onClick={loadLaunchNewsroom} disabled={!!busy} className="sm:ml-auto border border-gloss-gold text-gloss-gold px-6 py-3 overline flex items-center justify-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="load-launch-newsroom-btn">
                {busy === "launch" ? <RefreshCw size={16} className="animate-spin" /> : <Newspaper size={16} />} Load launch newsroom (newsroom only)
              </button>
              )}
            </div>
            {scope === "newsroom" && (
              <div className="mt-4 pt-4 border-t border-cream/15">
                {batch?.status === "running" ? (
                  <div data-testid="commission-nine-progress">
                    <p className="overline text-gloss-gold flex items-center gap-2"><RefreshCw size={13} className="animate-spin" /> The desk is writing — {batch.done || 0} of {batch.total || 9} pieces done</p>
                    <div className="h-1.5 bg-cream/15 mt-3"><div className="h-full bg-gloss-gold transition-all duration-700" style={{ width: `${((batch.done || 0) / (batch.total || 9)) * 100}%` }} /></div>
                    {(batch.items || []).some((x) => x.status === "writing") && (
                      <p className="font-mono text-[11px] text-cream/50 mt-2">Now writing: {(batch.items || []).filter((x) => x.status === "writing").map((x) => x.headline).join(" · ")}</p>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <button onClick={commissionNine} disabled={!!busy} className="bg-gloss-gold text-ink px-6 py-3 overline flex items-center justify-center gap-2 hover:bg-cream transition-colors disabled:opacity-60" data-testid="commission-nine-btn">
                      {busy === "nine" ? <RefreshCw size={16} className="animate-spin" /> : <Sparkles size={16} />} Commission 9 at once
                    </button>
                    <p className="font-sans text-xs text-cream/50">Scans today's UK small-business news, writes all 9 in the house voice with real photography and the right categories — lands below as drafts. {batch?.status === "done" && <span className="text-gloss-gold">Last batch: {batch.done}/{batch.total} delivered.</span>}{batch?.status === "failed" && <span className="text-red-400">Last batch failed — {batch.error}</span>}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="border border-ink/15" data-testid="articles-table">
          {list.map((a, idx) => (
            <div key={a.id} className="border-b border-ink/10 last:border-0">
            <div className="flex items-center justify-between gap-4 px-5 py-4">
              <div className="flex flex-col shrink-0 -my-1">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-ink/40 hover:text-ink disabled:opacity-20 transition-colors" aria-label="Move up" data-testid={`move-up-${a.id}`}><ChevronUp size={16} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} className="text-ink/40 hover:text-ink disabled:opacity-20 transition-colors" aria-label="Move down" data-testid={`move-down-${a.id}`}><ChevronDown size={16} /></button>
              </div>
              <div className="w-14 h-14 shrink-0 border border-ink/10 bg-ink/5 overflow-hidden hidden sm:block">
                {imgUrl(a) ? <img src={imgUrl(a)} alt="" className="w-full h-full object-cover" loading="lazy" /> : null}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3">
                  <span className="overline text-gloss-silver">{a.category || a.section}</span>
                  <Badge status={a.status} />
                  {a.status === "scheduled" && <span className="overline text-gloss-gold flex items-center gap-1"><Clock size={11} /> {(a.publish_at || "").slice(0, 16).replace("T", " ")}</span>}
                  {a.type === "member_feature" && <span className="overline text-ink/40">Feature</span>}
                  {a.is_archived && <span className="overline text-ink/40 flex items-center gap-1"><ArchiveIcon size={11} /> Archived</span>}
                </div>
                <p className="font-serif text-lg truncate mt-1">{a.title}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={`/read/${a.slug}`} target="_blank" rel="noreferrer" className="overline border border-ink/40 px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5" title="See exactly how it looks — photos, layout, everything (only you can see drafts)" data-testid={`preview-${a.id}`}><Eye size={13} /> See it</a>
                <button onClick={() => (a.editors_check && checkOpen !== a.id ? setCheckOpen(a.id) : a.editors_check ? setCheckOpen("") : runCheck(a))}
                  className="p-2 border border-ink/30 hover:border-ink transition-colors relative" aria-label="Editor's check"
                  title={a.editors_check ? "Editor's check — view report" : "Run editor's check (~1p)"} data-testid={`check-${a.id}`}>
                  <ShieldCheck size={15} className={checking === a.id ? "animate-pulse" : a.editors_check ? (a.editors_check.verdict === "ready" ? "text-green-700" : "text-amber-600") : ""} />
                </button>
                <button onClick={() => setEditor(a)} className="p-2 border border-ink/30 hover:border-ink transition-colors" aria-label="Edit" data-testid={`edit-${a.id}`}><Pencil size={15} /></button>
                <button onClick={() => togglePublish(a)} className="overline border border-ink px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors" data-testid={`toggle-${a.id}`}>{a.status === "published" ? "Unpublish" : "Publish"}</button>
                {(a.status === "published" || a.is_archived) && (
                  <button onClick={() => toggleArchive(a)} className="overline border border-ink/40 px-3 py-1.5 hover:bg-ink hover:text-cream transition-colors" data-testid={`archive-${a.id}`}>{a.is_archived ? "Restore" : "Archive"}</button>
                )}
                <button onClick={() => remove(a)} className="p-2 border border-ink/30 hover:border-red-500 hover:text-red-500 transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
            {checkOpen === a.id && (
              <div className="mx-5 mb-4 border border-ink/15 bg-secondary/60 p-4" data-testid={`check-report-${a.id}`}>
                {checking === a.id ? (
                  <p className="font-mono text-xs text-ink/60 flex items-center gap-2"><RefreshCw size={12} className="animate-spin" /> The sub-editor is reading the piece…</p>
                ) : a.editors_check ? (
                  <>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`overline text-[10px] px-2.5 py-1 ${a.editors_check.verdict === "ready" ? "bg-green-700 text-cream" : "bg-amber-500 text-ink"}`} data-testid={`check-verdict-${a.id}`}>
                        {a.editors_check.verdict === "ready" ? "Ready to publish" : "Needs attention"}
                      </span>
                      <span className="font-mono text-[10px] text-ink/45">Checked {(a.editors_check.checked_at || "").slice(0, 16).replace("T", " ")}</span>
                      <button onClick={() => runCheck(a)} className="overline text-[10px] px-2.5 py-1 border border-ink/30 hover:bg-ink hover:text-cream transition-colors" data-testid={`recheck-${a.id}`}>Re-check (~1p)</button>
                    </div>
                    <p className="font-sans text-sm mt-3">{a.editors_check.summary}</p>
                    {(a.editors_check.flags || []).length > 0 && (
                      <ul className="mt-3 space-y-1.5">
                        {a.editors_check.flags.map((f, i) => (
                          <li key={i} className="font-sans text-xs flex gap-2">
                            <span className={`shrink-0 overline text-[9px] px-1.5 py-0.5 ${f.severity === "warn" ? "bg-amber-500/20 text-amber-700" : "bg-ink/10 text-ink/60"}`}>{f.severity === "warn" ? "Check" : "Minor"}</span>
                            <span className="text-ink/75">{f.note}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {(a.editors_check.polish || []).length > 0 && (
                      <div className="mt-3">
                        <p className="overline text-[9px] text-ink/45 mb-1">To lift it further</p>
                        <ul className="space-y-1">
                          {a.editors_check.polish.map((p, i) => <li key={i} className="font-sans text-xs text-ink/70">· {p}</li>)}
                        </ul>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
            </div>
          ))}
          {list.length === 0 && <p className="px-5 py-10 text-center font-sans text-ink/50">{scope === "newsroom" ? "No newsroom articles yet — write one above." : scope === "members" ? "No member pieces yet." : "No editorials yet."}</p>}
        </div>
      </div>
      {editor && <ArticleEditor article={editor} onClose={() => setEditor(null)} onSaved={(doc) => { load(); setEditor(doc); }} />}
    </div>
  );
}

function ArticleEditor({ article, onClose, onSaved }) {
  const [a, setA] = useState(article);
  const [busy, setBusy] = useState("");
  const [gCount, setGCount] = useState(3);
  const [schedAt, setSchedAt] = useState((article.publish_at || "").slice(0, 16));
  useEffect(() => { setA(article); setSchedAt((article.publish_at || "").slice(0, 16)); }, [article]);
  const set = (k, v) => setA({ ...a, [k]: v });
  const isNew = a._new;

  const save = async () => {
    setBusy("save");
    const payload = { title: a.title, dek: a.dek, body: a.body, category: a.category, section: a.section, status: a.status,
      tags: Array.isArray(a.tags) ? a.tags : String(a.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
      seo_title: a.seo_title || "", seo_description: a.seo_description || "" };
    try {
      const r = isNew ? await client.post("/admin/articles", payload) : await client.patch(`/admin/articles/${a.id}`, payload);
      toast.success("Saved."); onSaved(r.data);
    } catch { toast.error("Save failed."); }
    setBusy("");
  };
  const uploadCover = async (files) => {
    if (!files?.[0]) return;
    if (isNew) { toast.error("Save the article first to add a cover."); return; }
    setBusy("cover");
    const fd = new FormData(); fd.append("file", files[0]);
    try { const r = await client.post(`/admin/articles/${a.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setA({ ...a, cover_image: r.data.cover_image }); toast.success("Cover uploaded."); onSaved({ ...a, cover_image: r.data.cover_image }); }
    catch { toast.error("Upload failed."); }
    setBusy("");
  };
  const setFrontCover = async () => {
    if (isNew) { toast.error("Save the article first."); return; }
    setBusy("front"); try { await client.post(`/admin/articles/${a.id}/set-cover`); setA({ ...a, is_cover: true, status: "published" }); toast.success("Set as homepage front cover."); onSaved({ ...a, is_cover: true, status: "published" }); } catch { toast.error("Failed."); } setBusy("");
  };
  const genGallery = async () => {
    if (isNew) { toast.error("Save the article first."); return; }
    setBusy("gallery");
    try { await client.post(`/admin/articles/${a.id}/generate-gallery`, { count: gCount }); toast.success(`Building ${gCount} varied image(s) — refresh in ~1-2 min.`); }
    catch { toast.error("Failed to queue image spread."); }
    setBusy("");
  };
  const schedule = async () => {
    if (isNew) { toast.error("Save the article first."); return; }
    setBusy("sched");
    try {
      const r = await client.post(`/admin/articles/${a.id}/schedule`, { publish_at: schedAt || null });
      setA(r.data); onSaved(r.data);
      toast.success(schedAt ? "Scheduled to auto-publish." : "Schedule cleared.");
    } catch { toast.error("Failed to schedule."); }
    setBusy("");
  };
  // ---- manual image management (gallery) ----
  const patchGallery = async (gallery, extra = {}) => {
    setA({ ...a, gallery, ...extra });
    try { await client.patch(`/admin/articles/${a.id}`, { gallery, ...extra }); onSaved({ ...a, gallery, ...extra }); }
    catch { toast.error("Image update failed."); }
  };
  const moveGalleryItem = (idx, dir) => {
    const g = [...(a.gallery || [])]; const j = idx + dir;
    if (j < 0 || j >= g.length) return;
    [g[idx], g[j]] = [g[j], g[idx]]; patchGallery(g);
  };
  const removeGalleryItem = (idx) => {
    const g = (a.gallery || []).filter((_, i) => i !== idx); patchGallery(g); toast.success("Image removed.");
  };
  const setGalleryAsCover = (idx) => {
    const img = a.gallery[idx]?.image; if (!img) return;
    patchGallery(a.gallery, { cover_image: img }); toast.success("Set as article cover.");
  };
  const uploadGalleryImage = async (files) => {
    if (!files?.[0]) return;
    if (isNew) { toast.error("Save the article first."); return; }
    setBusy("gupload");
    const fd = new FormData(); fd.append("file", files[0]);
    try { const r = await client.post(`/admin/articles/${a.id}/gallery-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setA({ ...a, gallery: r.data.gallery }); onSaved({ ...a, gallery: r.data.gallery }); toast.success("Image added to spread."); }
    catch { toast.error("Upload failed."); }
    setBusy("");
  };

  const tagsStr = Array.isArray(a.tags) ? a.tags.join(", ") : (a.tags || "");
  const gallery = a.gallery || [];

  return (
    <Drawer onClose={onClose} title={isNew ? "New Editorial" : "Edit Editorial"} wide>
      <div className="space-y-5">
        <div className="aspect-[16/7] bg-ink overflow-hidden relative">
          {imgUrl(a) ? <img src={imgUrl(a)} alt="cover" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><span className="font-mono text-xs text-cream/40 uppercase tracking-widest">No cover image</span></div>}
          <label className="absolute bottom-3 right-3 bg-cream text-ink px-4 py-2 overline flex items-center gap-2 cursor-pointer hover:bg-gloss-silver transition-colors" data-testid="article-cover-upload">
            {busy === "cover" ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />} {imgUrl(a) ? "Replace" : "Upload"} cover
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadCover(e.target.files)} disabled={busy} />
          </label>
        </div>
        <Field label="Headline" v={a.title || ""} on={(v) => set("title", v)} testid="ae-title" />
        <Field label="Standfirst / dek" v={a.dek || ""} on={(v) => set("dek", v)} testid="ae-dek" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Select label="Category" v={a.category || "Trends"} on={(v) => set("category", v)} options={CATEGORIES} testid="ae-category" />
          <Select label="Section (placement)" v={a.section || "Culture"} on={(v) => set("section", v)} options={SECTIONS} testid="ae-section" />
          <Select label="Status" v={a.status || "draft"} on={(v) => set("status", v)} options={["draft", "published", "scheduled"]} testid="ae-status" />
        </div>
        <Field label="Tags (comma separated)" v={tagsStr} on={(v) => set("tags", v)} testid="ae-tags" />

        {!isNew && (
          <div className="border border-ink/15 bg-white p-4" data-testid="schedule-panel">
            <p className="overline text-ink/50 mb-2 flex items-center gap-2"><Clock size={13} /> Schedule auto-publish {a.status === "scheduled" && <span className="text-gloss-gold">· scheduled</span>}</p>
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} className="flex-1 border border-ink/40 bg-white px-3 py-2.5 font-sans focus:outline-none focus:border-ink" data-testid="schedule-input" />
              <button onClick={schedule} disabled={busy} className="bg-ink text-cream px-5 py-2.5 overline flex items-center justify-center gap-2 hover:bg-gloss-silver hover:text-ink transition-colors disabled:opacity-60" data-testid="schedule-btn">
                {busy === "sched" ? <RefreshCw size={14} className="animate-spin" /> : <Clock size={14} />} {schedAt ? "Schedule" : "Clear"}
              </button>
            </div>
            <p className="font-mono text-[11px] text-ink/45 mt-2">Times are UTC. The article publishes automatically at this time (checked every minute).</p>
          </div>
        )}

        {!isNew && (
          <button onClick={async () => { const v = a.hero_fit === "headroom" ? "" : "headroom"; try { const r = await client.patch(`/admin/articles/${a.id}`, { hero_fit: v }); setA(r.data); onSaved(r.data); toast.success(v ? "Full headroom on — this piece only." : "Back to the standard crop."); } catch { toast.error("Couldn't update."); } }}
            className={`w-full flex items-center justify-center gap-2 py-3 overline border transition-colors ${a.hero_fit === "headroom" ? "bg-gloss-gold text-ink border-gloss-gold" : "border-ink/40 hover:border-ink"}`} data-testid="ae-headroom-toggle">
            {a.hero_fit === "headroom" ? "Full headroom: ON (this piece only)" : "Fix cut-off head — full headroom (this piece only)"}
          </button>
        )}

        {!isNew && (
          <button onClick={setFrontCover} disabled={busy} className={`w-full flex items-center justify-center gap-2 py-3 overline border transition-colors disabled:opacity-60 ${a.is_cover ? "bg-gloss-gold text-ink border-gloss-gold" : "border-ink/40 hover:border-ink"}`} data-testid="set-cover">
            {busy === "front" ? <RefreshCw size={14} className="animate-spin" /> : <Star size={14} />} {a.is_cover ? "Current homepage front cover" : "Set as homepage front cover"}
          </button>
        )}

        {!isNew && (
          <div className="border border-ink/15 bg-white p-4" data-testid="image-manager">
            <p className="overline text-ink/50 mb-3 flex items-center gap-2"><ImageIcon size={13} /> Image spread — {gallery.length} image(s)</p>
            {gallery.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-4">
                {gallery.map((g, i) => (
                  <div key={i} className="relative group border border-ink/10" data-testid={`gallery-item-${i}`}>
                    <img src={assetUrl(g.image)} alt={g.slug} className="w-full aspect-square object-cover" />
                    <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => moveGalleryItem(i, -1)} disabled={i === 0} className="p-1.5 text-white disabled:opacity-30 hover:text-gloss-gold" title="Move earlier" data-testid={`gallery-up-${i}`}><ChevronUp size={15} /></button>
                      <button onClick={() => setGalleryAsCover(i)} className="p-1.5 text-white hover:text-gloss-gold" title="Use as cover" data-testid={`gallery-cover-${i}`}><Star size={15} /></button>
                      <button onClick={() => moveGalleryItem(i, 1)} disabled={i === gallery.length - 1} className="p-1.5 text-white disabled:opacity-30 hover:text-gloss-gold" title="Move later" data-testid={`gallery-down-${i}`}><ChevronDown size={15} /></button>
                      <button onClick={() => removeGalleryItem(i)} className="p-1.5 text-white hover:text-red-400" title="Remove" data-testid={`gallery-remove-${i}`}><X size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-col sm:flex-row items-stretch gap-2">
              <label className="flex-1 flex items-center justify-center gap-2 py-2.5 overline border border-ink/40 hover:border-ink transition-colors cursor-pointer" data-testid="gallery-upload">
                {busy === "gupload" ? <RefreshCw size={14} className="animate-spin" /> : <Upload size={14} />} Upload own image
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadGalleryImage(e.target.files)} disabled={busy} />
              </label>
              <select value={gCount} onChange={(e) => setGCount(Number(e.target.value))} className="border border-ink/40 bg-white px-3 overline focus:outline-none focus:border-ink" data-testid="gallery-count-select">
                {[1, 2, 3].map((n) => <option key={n} value={n}>{n} img</option>)}
              </select>
              <button onClick={genGallery} disabled={busy} className="flex-1 flex items-center justify-center gap-2 py-2.5 overline border border-ink/40 hover:border-ink transition-colors disabled:opacity-60" data-testid="generate-gallery">
                {busy === "gallery" ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />} {gallery.length ? "Regenerate (AI)" : "Generate AI spread"}
              </button>
            </div>
            <p className="font-mono text-[11px] text-ink/45 mt-2">Hover an image to reorder (↑↓), set it as the cover (★) or remove it (✕). Upload your own or generate a varied AI spread.</p>
          </div>
        )}

        <Area label="Body (Markdown — use ## for headings, > for pull quotes)" v={a.body || ""} on={(v) => set("body", v)} rows={16} testid="ae-body" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="SEO title" v={a.seo_title || ""} on={(v) => set("seo_title", v)} testid="ae-seo-title" />
          <Field label="SEO description" v={a.seo_description || ""} on={(v) => set("seo_description", v)} testid="ae-seo-desc" />
        </div>
        <div className="sticky bottom-0 bg-cream pt-4 pb-2 border-t border-ink/15">
          <button onClick={save} disabled={busy} className="w-full bg-ink text-cream py-4 overline flex items-center justify-center gap-2 hover:bg-gloss-silver hover:text-ink transition-colors disabled:opacity-60" data-testid="save-article">
            {busy === "save" ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />} {isNew ? "Create article" : "Save changes"}
          </button>
        </div>
      </div>
    </Drawer>
  );
}


/* ---------------- Social ---------------- */
